/**
 * The Programme Viewer's versions and its programme learning outcomes.
 *
 * @module components/reports/ProgrammeStructure
 */

import React from "react";

import {
  Badge,
  CollectionView,
  type Column,
  countLabel,
  EmptyState,
  MutedText,
  Panel,
  Text,
} from "@/components/ui";
import type { Module, Programme, ProgrammeVersion, Stage } from "@/types";

/** A stage names its modules two ways; both count, and neither twice. */
function resolveStageModules(stage: Stage, p: Programme): Module[] {
  const byId = new Map((p.modules ?? []).map((m) => [m.id, m]));
  const ids: string[] = [];
  (stage.modules ?? []).forEach((entry) => ids.push(entry.moduleId));
  (stage.moduleIds ?? []).forEach((id) => {
    if (!ids.includes(id)) {
      ids.push(id);
    }
  });
  return ids.map((id) => byId.get(id)).filter((m): m is Module => Boolean(m));
}

function stageColumns(p: Programme): readonly Column<Stage>[] {
  return [
    { key: "stage", header: "Stage", render: (s) => s.name ?? `Stage ${s.sequence ?? "?"}` },
    { key: "target", header: "Credits target", render: (s) => s.creditsTarget ?? "—" },
    {
      key: "modules",
      header: "Modules",
      render: (s) => countLabel(resolveStageModules(s, p).length, "module"),
    },
  ];
}

function VersionPanel({
  version: v,
  programme: p,
}: {
  version: ProgrammeVersion;
  programme: Programme;
}): React.ReactElement {
  const stages = v.stages ?? [];
  return (
    <Panel
      tone="inset"
      headingLevel={3}
      flush
      title={
        <>
          {v.label || v.code || "Unnamed version"}
          {v.deliveryModality && (
            <Badge tone="ink" variant="outline" size="sm" className="ml-2">
              {v.deliveryModality}
            </Badge>
          )}
        </>
      }
    >
      <CollectionView
        storageKey="versions-section"
        columns={stageColumns(p)}
        rows={stages}
        rowKey={(s) => s.id}
        caption={`Stages in ${v.label || v.code || "this version"}`}
        density="sm"
        emptyState={<EmptyState title="No stages." density="row" />}
      />
    </Panel>
  );
}

export function ProgrammeVersions({
  programme: p,
}: {
  programme: Programme;
}): React.ReactElement | null {
  const versions = p.versions ?? [];
  if (versions.length === 0) {
    return null;
  }

  return (
    <Panel
      icon="git-branch"
      title="Versions and stages"
      headingLevel={2}
      data-testid="versions-section"
    >
      <div className="space-y-3">
        {versions.map((v) => (
          <VersionPanel key={v.id} version={v} programme={p} />
        ))}
      </div>
    </Panel>
  );
}

interface PloRow {
  id: string;
  code: string;
  text: string;
  mapped: number;
}

const PLO_COLUMNS: readonly Column<PloRow>[] = [
  {
    key: "code",
    header: "Code",
    width: "w-24",
    render: (row) => (
      <Text as="span" size="sm" className="font-mono">
        {row.code}
      </Text>
    ),
  },
  { key: "text", header: "Learning outcome", render: (row) => row.text },
  {
    key: "mapped",
    header: "Mapped module outcomes",
    align: "end",
    sortValue: (row) => row.mapped,
    render: (row) =>
      row.mapped > 0 ? (
        <Badge tone="ink" variant="outline" size="sm">
          {row.mapped}
        </Badge>
      ) : (
        <MutedText as="span" size="caption">
          none
        </MutedText>
      ),
  },
];

export function ProgrammeOutcomes({
  programme: p,
}: {
  programme: Programme;
}): React.ReactElement | null {
  const plos = p.plos ?? [];
  if (plos.length === 0) {
    return null;
  }

  const mappings = p.ploToMimlos ?? {};
  const rows: PloRow[] = plos.map((plo, i) => ({
    id: plo.id,
    code: (plo.code ?? "").trim() || `PLO${i + 1}`,
    text: plo.text,
    mapped: (mappings[plo.id] ?? []).length,
  }));

  return (
    <Panel
      icon="target"
      title={`Programme learning outcomes (${plos.length})`}
      headingLevel={2}
      flush
      data-testid="plo-section"
    >
      <CollectionView
        storageKey="report-structure-plos"
        columns={PLO_COLUMNS}
        rows={rows}
        rowKey={(row) => row.id}
        caption={`Programme learning outcomes for ${p.title}`}
        density="sm"
        verticalAlign="top"
        emptyState={<EmptyState title="No outcomes." density="row" />}
      />
    </Panel>
  );
}
